/**
 * Turn a block's text plus a set of spans into renderable segments.
 *
 * This is the browser half of the evidence contract, and the one class of bug an
 * API-level test cannot catch. Postgres and Python both count **Unicode code points**;
 * JavaScript strings are UTF-16. `"🙂ок".slice(1, 3)` does not return `"ок"` — it
 * returns half a surrogate followed by `о`. So a single emoji anywhere before a match
 * shifts the painted highlight by one character, silently, and only in the documents
 * that happen to contain one.
 *
 * The defence here is to stop indexing UTF-16 entirely: `Array.from` yields code
 * points, so a code-point offset is simply an array index. There is no conversion to
 * get wrong.
 *
 * Spans may overlap (two search terms in one sentence, two citations over the same
 * clause). They are merged before segmenting, because a nested `<mark>` renders as a
 * darker patch that reads as a *third*, stronger kind of evidence — which it is not.
 */

export interface Span {
  readonly start_cp: number;
  readonly end_cp: number;
  readonly quote: string;
}

export interface Segment {
  readonly text: string;
  readonly marked: boolean;
}

/** Merge overlapping and adjacent ranges into a minimal sorted set. */
export function mergeRanges(spans: readonly Span[]): Array<[number, number]> {
  const sorted = spans
    .map((s): [number, number] => [s.start_cp, s.end_cp])
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  const merged: Array<[number, number]> = [];
  for (const [start, end] of sorted) {
    const last = merged[merged.length - 1];
    // Touching ranges merge too: `[0,3]` and `[3,5]` are one contiguous highlight, and
    // splitting them would put a seam in the middle of a word.
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

/**
 * Segment `text` into marked and unmarked runs.
 *
 * Ranges outside the text are clamped rather than throwing: a span that does not fit
 * is a data bug worth surfacing through `spanMismatch`, but it must not blank the
 * whole panel and leave the investigator with nothing to read.
 */
export function segment(text: string, spans: readonly Span[]): Segment[] {
  const points = Array.from(text); // code points, so offsets are plain indices
  const ranges = mergeRanges(spans);
  const segments: Segment[] = [];

  let cursor = 0;
  for (const [rawStart, rawEnd] of ranges) {
    const start = Math.max(0, Math.min(rawStart, points.length));
    const end = Math.max(start, Math.min(rawEnd, points.length));
    if (end === start) continue;
    if (start > cursor) segments.push({ text: points.slice(cursor, start).join(""), marked: false });
    segments.push({ text: points.slice(start, end).join(""), marked: true });
    cursor = end;
  }
  if (cursor < points.length) {
    segments.push({ text: points.slice(cursor).join(""), marked: false });
  }
  return segments;
}

/**
 * The spans whose offsets do not reproduce their own quote in this text.
 *
 * A non-empty result means the panel is about to lie. Callers show the mismatch
 * instead of the highlight: a highlight that disagrees with its evidence is worse for
 * an investigation than no highlight at all, because it looks like a finding.
 */
export function spanMismatch(text: string, spans: readonly Span[]): Span[] {
  const points = Array.from(text);
  return spans.filter((s) => points.slice(s.start_cp, s.end_cp).join("") !== s.quote);
}

/** A short excerpt centred on a span, for list rows that cannot show a whole block. */
export function excerpt(text: string, span: Span, radius = 60): Segment[] {
  const points = Array.from(text);
  const start = Math.max(0, span.start_cp - radius);
  const end = Math.min(points.length, span.end_cp + radius);
  const sliced = points.slice(start, end).join("");
  const shifted = { ...span, start_cp: span.start_cp - start, end_cp: span.end_cp - start };
  const body = segment(sliced, [shifted]);
  const head: Segment[] = start > 0 ? [{ text: "…", marked: false }] : [];
  const tail: Segment[] = end < points.length ? [{ text: "…", marked: false }] : [];
  return [...head, ...body, ...tail];
}
